import { useState } from 'react';
import { AirParticles } from '@/components/Layout';
import { Send, Mail, Phone, MapPin, Check } from 'lucide-react';
import logger from '@/utils/logger';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', inquiry_type: 'general' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', message: '', inquiry_type: 'general' });
    } catch (err) {
      logger.error('Contact form submission failed:', err.message);
    }
    setLoading(false);
  };

  return (
    <div>
      <section className="relative pt-32 pb-20 px-6 lg:px-12 overflow-hidden" style={{ background: 'var(--soft-white)' }} data-testid="contact-hero">
        <AirParticles count={8} color="rgba(15,76,92,0.04)" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="overline-teal mb-4 animate-fade-up">Get In Touch</div>
          <h1 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6 animate-fade-up delay-100" style={{ fontFamily: 'Inter' }} data-testid="contact-title">
            Let's build a cleaner<br /><span style={{ color: 'var(--aqua)' }}>future together</span>.
          </h1>
        </div>
      </section>

      <section className="section-padding px-6 lg:px-12" style={{ marginTop: '-60px' }} data-testid="contact-form-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Inter' }}>Reach us at</h3>
                <div className="space-y-4">
                  {[
                    { icon: Mail, label: 'info@jagdaleair.com', color: 'var(--deep-teal)' },
                    { icon: Phone, label: '+91 91 3758 9191', color: 'var(--aqua)' },
                    { icon: MapPin, label: 'Mumbai, Maharashtra, India', color: '#8B5CF6' },
                  ].map((c) => (
                    <div key={c.label} className="flex items-center gap-3" data-testid={`contact-info-${c.label}`}>
                      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${c.color}10` }}>
                        <c.icon className="w-4 h-4" style={{ color: c.color }} strokeWidth={1.5} />
                      </div>
                      <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{c.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Inter' }}>Quick links</h3>
                <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <p>For B2B inquiries, visit our <a href="/builders" className="font-medium underline" style={{ color: 'var(--aqua)' }}>Builders page</a></p>
                  <p>For investment, visit our <a href="/investor" className="font-medium underline" style={{ color: 'var(--aqua)' }}>Investor page</a></p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-3 card-hover p-8" data-testid="contact-form-card">
              {submitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'rgba(16,185,129,0.08)' }}>
                    <Check className="w-6 h-6" style={{ color: 'var(--green-good)' }} />
                  </div>
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Inter' }}>Message sent!</h3>
                  <p className="text-sm mb-6" style={{ color: 'var(--text-secondary)' }}>We'll respond within 24 hours.</p>
                  <button onClick={() => setSubmitted(false)} className="btn-outline text-sm" data-testid="send-another-btn">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div>
                    <label className="block text-xs font-medium mb-1.5 uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Inquiry Type</label>
                    <select data-testid="contact-type" value={form.inquiry_type} onChange={e => setForm({...form, inquiry_type: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border)', background: 'var(--soft-white)' }}>
                      <option value="general">General Inquiry</option>
                      <option value="product">Product Information</option>
                      <option value="demo">Request Demo</option>
                      <option value="partnership">Partnership</option>
                      <option value="support">Support</option>
                    </select>
                  </div>
                  <input data-testid="contact-name" type="text" required placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border)', background: 'var(--soft-white)' }} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <input data-testid="contact-email" type="email" required placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border)', background: 'var(--soft-white)' }} />
                    <input data-testid="contact-phone" type="tel" placeholder="Phone (optional)" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm" style={{ border: '1px solid var(--border)', background: 'var(--soft-white)' }} />
                  </div>
                  <textarea data-testid="contact-message" required rows={4} placeholder="Your message..." value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full px-4 py-3 rounded-xl text-sm resize-none" style={{ border: '1px solid var(--border)', background: 'var(--soft-white)' }} />
                  <button type="submit" disabled={loading} className="btn-vayu w-full flex items-center justify-center gap-2" data-testid="contact-submit" style={{ opacity: loading ? 0.7 : 1 }}>
                    <Send className="w-4 h-4" /> {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
